import React from 'react';
import DOMPurify from 'dompurify';
import Star from './components/star';

import './session.scss';

export default class Session extends React.Component {
    static defaultProps = {
        watch: 'Watch video',
        download: 'Download slides',
        needSocialLoginIn: '',
        data: null,
        compact: false,
        removeSectionTimeWithoutTitle: true,
        speakerFormat: {
            fullFormat: true,
            connector: ' | ',
            separator: ','
        }
    }
    constructor(props) {
        super(props);
        this.state = { playEmbed: false };
    }

    render() {
        const { data, compact } = this.props;
        if (!data) return null;

        return compact ? this.renderCompactSession(data) : this.renderSession(data, this.props.color);
    }

    renderCompactSession(data) {
        const title = this.renderTitle(data.title);
        const video = this.renderVideo(data.videoUrl, this.props.embedVideo);
        const embeddedVideo = this.renderEmbeddedVideo(data.videoUrl, data.title);
        const download = this.renderPresentation(data.presentationUrl);
        const speakers = this.renderSpeakers(data.speakers);

        return (
            <div className='row compact-session'>
                <div className='col-12'>
                    {title}
                </div>
                <div className='col-12 col-md-4'>
                    {speakers}
                </div>
                <div className='col-12 col-md-4'>
                    {video}
                    {embeddedVideo}
                </div>
                <div className='col-12 col-md-4'>
                    {download}
                </div>
            </div>
        );
    }

    renderSession(data, color) {
        const safeDescription = DOMPurify.sanitize((data && data.description) || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
        const timeText = this.getSessionTime(data);
        const time = this.renderTime(timeText, data);
        const title = this.renderTitle(data.title, color);
        const video = this.renderVideo(data.videoUrl, this.props.embedVideo);
        const embeddedVideo = this.renderEmbeddedVideo(data.videoUrl, data.title);
        const download = this.renderPresentation(data.presentationUrl);
        const location = this.renderLocation(data.location);
        const speakers = this.renderSpeakers(data.speakers);
        const sessionId = this.renderSessionId(data.sessionId);
        const star = this.renderStar(data);
        const timeColClassName = this.props.removeSectionTimeWithoutTitle && !data.title ? 'col-12' : 'col-12 col-lg-3 col-md-4';
        const infoColClassName = this.props.removeSectionTimeWithoutTitle && !data.title ? 'col-12 info-whole-col' : 'col-12 col-lg-9 col-md-8'
        const bookCol = data.title ? (<div className='book-col'>{star}</div>) : null;
        return (
            <div className='row session'>
                <div className={timeColClassName}>
                    {time}
                </div>
                <div className={infoColClassName}>
                    <div className='info-row'>
                        <div className='content-col'>
                            {title}
                            <div className='line'>
                                {location}
                                {speakers}
                                {sessionId}
                            </div>
                            <div className='line'>
                                {video}
                                {embeddedVideo}
                                {download}
                            </div>
                            <div className='line'>
                                <div className='desc'>{safeDescription}</div>
                            </div>
                        </div>
                        {bookCol}
                    </div>
                </div>
            </div>
        );
    }

    renderStar(data, tipPosition = 'bottom') {
        if(!data.title) return null;

        if (this.props.hideBookmarks){
            return null;
        }

        return (
            <Star tipPosition={tipPosition}
                tips={this.props.book}
                data={data}
                active={data.booked}
                onBeforeChange={this.props.onBeforeUpdateBookmark}
                onChange={this.props.onUpdateBookmark} />
        );
    }

    renderTime(text, data) {
        if (!text || !data.title) return null;

        const star = this.renderStar(data, 'left');

        return (
            <div className='time-col'>
                <div className='time'>{text}</div>
                {star}
            </div>
        );
    }

    renderTitle(data, color) {
        if (!data) return null;

        const titleStyle = color && { color };
        const safeTitle = DOMPurify.sanitize(data || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
        return (
            <div className='title' style={titleStyle}>{safeTitle}</div>
        );
    }

    renderLocation(data) {
        if (!(data || '').trim()) return null;

        const safeLocation = DOMPurify.sanitize(data || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

        return (
            <div className='field location'>
                <i className="fas fa-map-marker-alt" />
                <span>{safeLocation}</span>
            </div>
        );
    }

    renderVideo(data, isEmbed=false) {
        //TODO tracking data, send event on click
        //wait for event before showing embed video
        if (!data) return null;
        const rawVideoUrl = (data || '').toString().trim();
        if (!rawVideoUrl) return null;
        if (!/^https?:\/\//i.test(rawVideoUrl)) return null;

        let safeUrl;
        try {
            const parsedVideoUrl = new URL(rawVideoUrl, window.location.origin);
            const videoProtocol = (parsedVideoUrl.protocol || '').toLowerCase();
            if (videoProtocol !== 'http:' && videoProtocol !== 'https:') {
                return null;
            }
            safeUrl = parsedVideoUrl.href;
        } catch (error) {
            return null;
        }
        const handleVideoClick = (e) => {
            const needsLogin = this.props.needSocialLoginIn == 'true'
                || this.props.needSocialLoginIn == 'all'
                || this.props.needSocialLoginIn == 'video';

            if (needsLogin && window.PlatformSDK) {
                e.preventDefault();
                if (!window.PlatformSDK.isLoggedIn()) {
                    const height = window.pageYOffset !== undefined ? window.pageYOffset : window.scrollTop;
                    window.PubHub.util.cookie.setCookie('pubhub_agenda_video_link', `${safeUrl} ${height}`);
                    window.PlatformSDK.openLogin();
                } else if (isEmbed) {
                    this.setState({ showVideoOverlay: true });
                }
            } else if (isEmbed) {
                e.preventDefault();
                this.setState({ showVideoOverlay: true });
            }
        };

        return (
            <a href={safeUrl} target='_blank' rel='noopener noreferrer'
                onClick={handleVideoClick}
                className='field video'
                data-track-id='pubhub-agenda:watch-video'
                data-track-name='pubhub-agenda:watch-video-link'
                data-track-value={safeUrl}>
                <i className='far fa-play-circle' />
                <span>{this.props.watch}</span>
            </a>
        );
    }

    renderEmbeddedVideo(data, title) {
        if (!data) return null;
        if (!this.state.showVideoOverlay) return null;
        const clickAway = () => {
            this.setState({showVideoOverlay: false});
        }
        function getId(url) {
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            console.log('result: ', match[2]);
            return (match && match[2].length === 11)
                ? match[2]
                : null;
        }
        return (
            <div onClick={clickAway} className={`video-overlay ${this.state.showVideoOverlay ? 'show' : ''}`}>
                <div className='video-container'>
                    <i className='far fa-times video-closed' onClick={clickAway} ></i>
                    <div className='video-title'>{title}</div>
                    <div className='videowrapper'>
                        <iframe
                            // width="1120"
                            // height="630"
                            width="100%" height="100%" 
                            src={'https://www.youtube.com/embed/' + getId(data) +'?autoplay=1'}
                            title="YouTube video player"
                            frameBorder="1"
                            autop
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen />
                    </div>
                </div>
            </div>
        );
    }

    renderPresentation(data) {
        if (!data) return null;

        const isArray = Array.isArray(data);

        if (isArray && data.length == 0) return null;

        // const title = isArray ? data[0].title : this.props.download;
        const title = this.props.download;
        const rawUrl = isArray ? data[0].url : data;
        const inputUrl = (rawUrl || '').toString().trim();
        if (!inputUrl) return null;
        if (!/^https?:\/\//i.test(inputUrl)) return null;

        let url;
        try {
            const parsedUrl = new URL(inputUrl, window.location.origin);
            const protocol = (parsedUrl.protocol || '').toLowerCase();
            if (protocol !== 'http:' && protocol !== 'https:') {
                return null;
            }
            url = parsedUrl.href;
        } catch (error) {
            return null;
        }
        const handlePresentationClick = (e) => {
            const needsLogin = this.props.needSocialLoginIn == 'true'
                || this.props.needSocialLoginIn == 'all'
                || this.props.needSocialLoginIn == 'presentation';

            if (needsLogin && window.PlatformSDK) {
                e.preventDefault();
                if (!window.PlatformSDK.isLoggedIn()) {
                    const height = window.pageYOffset !== undefined ? window.pageYOffset : window.scrollTop;
                    window.PubHub.util.cookie.setCookie('pubhub_agenda_download_link', `${url} ${height}`);
                    window.PlatformSDK.openLogin();
                }
            }
        };

        return (
            <a href={url} target='_blank' rel='noopener noreferrer'
                onClick={handlePresentationClick}
                className='field download'
                data-track-id='pubhub-agenda:download'
                data-track-name='pubhub-agenda:download-link'
                data-track-value={url}>
                <i className='fas fa-arrow-down' />
                <span>{title}</span>
            </a>
        )
    }

    renderSpeakers(data) {
        if (!data || data.length == 0) return null;

        const speakers = (data || []).map(i => this.renderSpeaker(i));
        return (
            <div className='field speakers' onClick={this.onClickSpeaker}>
                <i className="fas fa-user" />
                <span className='list'>{speakers}</span>
            </div>
        );
    }

    renderSpeaker(i) {
        if (!i) return null;

        const { fullFormat, connector, separator } = this.props.speakerFormat;
        const className = 'speaker' + (i.noDetail ? '' : ' has-detail');
        const content = i.fullName + (fullFormat && i.organization ? (connector + i.organization) : '');
        return <div key={i.id} data-separator={separator} className={className} data-speaker={i.id}
         data-track-id="pubhub-agenda:speaker" data-track-name="pubhub-agenda: speaker-name" data-track-value={content}>{content}</div>;
    }

    renderSessionId(data) {
        if (!data) return null;

        return (
            <div className='field session-id'>
                <i className="far fa-calendar" />
                <span>{data}</span>
            </div>
        )
    }

    getSessionTime(data) {
        const time = data.end ? (data.start + ' - ' + data.end) : data.start;
        return time.replace(/ AM/g, 'am').replace(/ PM/g, 'pm');;
    }

    onClickSpeaker = (e) => {
        if (!e.target.dataset.speaker || !this.props.onClickSpeaker) {
            return;
        }

        this.props.onClickSpeaker(e.target.dataset.speaker);
    }
}
